import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { getEvents, getLatestVideo } from '@/lib/api';
import { formatDate } from '@/lib/utils';

export default function HomePage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [latestVideo, setLatestVideo] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [eventsRes, videoRes] = await Promise.all([
          getEvents(),
          getLatestVideo(),
        ]);
        setEvents(eventsRes.data.events || []);
        setLatestVideo(videoRes.data);
      } catch (e) {
        console.error('Failed to fetch data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const now = new Date();
  const upcomingEvents = events
    .filter(e => new Date(e.start_at) > now)
    .sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
  const heroEvent = upcomingEvents[0];
  const secondEvent = upcomingEvents[1];

  return (
    <div className="bg-white" data-testid="homepage">
      {/* ========== SECTION 1: Hero Feature (Book/Product style) ========== */}
      <section className="py-24 sm:py-32 lg:py-40" data-testid="homepage-hero-section">
        <div className="mx-auto w-full max-w-5xl px-6 sm:px-10">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16 lg:gap-20">
            {/* Left: Editorial poster cards - like Lovy Elias book display */}
            <div className="w-full md:w-5/12 flex-shrink-0">
              <div className="relative flex justify-center items-center py-6">
                {/* Main poster card */}
                <Link
                  to={heroEvent ? `/events/${heroEvent.id}` : '/revival-night'}
                  className="block group relative z-10"
                  data-testid="homepage-hero-poster-link"
                >
                  <div className="bg-[#f5f2ec] p-5 sm:p-6 shadow-sm w-[240px] sm:w-[280px] transform -rotate-1 group-hover:rotate-0 transition-transform duration-300">
                    <div className="border border-gray-200/60 p-4 sm:p-5 text-center">
                      <p className="font-body text-[9px] uppercase tracking-[0.25em] text-gray-400 mb-3">
                        Prophet Malluc Ministries
                      </p>
                      <div className="w-10 h-px bg-gray-300 mx-auto mb-4" />
                      <h3
                        className="font-heading text-xl sm:text-2xl font-medium text-gray-800 leading-tight italic"
                        data-testid="homepage-hero-poster-title"
                      >
                        3000 John F. Kennedy Blvd, Jersey City, NJ 07306 Floor 3
                      </h3>
                      <p className="font-body text-[10px] uppercase tracking-[0.2em] text-gray-400 mt-3">
                        {heroEvent ? heroEvent.location?.split(',')[0] : '5:30pm'}
                      </p>
                      <div className="w-10 h-px bg-gray-300 mx-auto mt-4 mb-3" />
                      <p className="font-body text-[9px] uppercase tracking-[0.15em] text-gray-400">
                        {heroEvent ? formatDate(heroEvent.start_at) : '2026'}
                      </p>
                    </div>
                  </div>
                </Link>

                {/* Second offset poster card (decorative) */}
                {secondEvent && (
                  <div className="absolute top-10 -right-2 sm:right-0 z-0 opacity-60">
                    <div className="bg-[#f0ede7] p-4 sm:p-5 shadow-sm w-[200px] sm:w-[230px] transform rotate-3">
                      <div className="border border-gray-200/40 p-3 sm:p-4 text-center">
                        <p className="font-body text-[8px] uppercase tracking-[0.25em] text-gray-400 mb-2">
                          Prophet Malluc Ministries
                        </p>
                        <div className="w-8 h-px bg-gray-300 mx-auto mb-3" />
                        <h3 className="font-heading text-lg font-medium text-gray-700 leading-tight italic">
                          3000 John F. Kennedy Blvd, Jersey City, NJ 07306 Floor 3
                        </h3>
                        <p className="font-body text-[9px] uppercase tracking-[0.2em] text-gray-400 mt-2">
                          {secondEvent.location?.split(',')[0]}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right: Text content */}
            <div className="w-full md:w-7/12">
              <h1
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-medium text-gray-900 leading-tight"
                data-testid="homepage-hero-heading"
                style={{ fontStyle: 'italic' }}
              >
                Experience the Prophetic
              </h1>

              <div className="mt-8 space-y-4">
                <p className="text-[15px] text-gray-500 font-body leading-relaxed">
                  Step into a new dimension of spiritual encounters with Prophet Malluc. Our events are designed to ignite your faith, 
                  awaken your prophetic gift, and bring you into a deeper relationship with God.
                </p>
                <p className="text-[15px] text-gray-500 font-body leading-relaxed">
                  Whether you are seeking healing, direction, or a fresh encounter with the Holy Spirit,
                  there is a place for you. Join thousands who have experienced the transformative power of God
                  through Revival Nights, Prophetic Schools, and one-on-one Mentorship.
                </p>
              </div>

              {heroEvent && (
                <div className="mt-10">
                  <Link to={`/events/${heroEvent.id}`}>
                    <button
                      className="inline-flex items-center h-12 px-8 border border-gray-900 text-gray-900 font-body text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-gray-900 hover:text-white transition-colors duration-200"
                      data-testid="homepage-hero-cta-button"
                    >
                      View Upcoming Events
                    </button>
                  </Link>
                </div>
              )}

              {!heroEvent && !loading && (
                <div className="mt-10">
                  <Link to="/revival-night">
                    <button
                      className="inline-flex items-center h-12 px-8 border border-gray-900 text-gray-900 font-body text-[13px] font-medium uppercase tracking-[0.15em] hover:bg-gray-900 hover:text-white transition-colors duration-200"
                      data-testid="homepage-hero-cta-button"
                    >
                      View Upcoming Events
                    </button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ========== SECTION 2: Latest Message (Full-width immersive) ========== */}
      <section
        className="relative w-full overflow-hidden"
        data-testid="homepage-latest-event-section"
        style={{ minHeight: '50vh' }}
      >
        {/* Background image from YouTube thumbnail */}
        {latestVideo?.thumbnail && (
          <div
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url('${latestVideo.thumbnail}')`,
            }}
          />
        )}
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-black/50" />

        {/* Content */}
        <div className="relative z-10 flex flex-col items-center justify-center text-center px-6 sm:px-10" style={{ minHeight: '50vh' }}>
          <p
            className="font-heading text-white/70 text-lg sm:text-xl italic"
            data-testid="homepage-latest-event-label"
          >
            Latest Message
          </p>

          <h2
            className="font-heading text-white text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-medium mt-4 uppercase max-w-4xl"
            style={{ letterSpacing: '0.06em' }}
            data-testid="homepage-latest-event-title"
          >
            {latestVideo?.title || 'Watch Live'}
          </h2>

          <div className="mt-8">
            <a
              href={latestVideo?.url || 'https://www.youtube.com/@PMalluc/streams'}
              target="_blank"
              rel="noopener noreferrer"
            >
              <button
                className="inline-flex items-center h-11 px-7 border border-white/50 text-white font-body text-[12px] font-medium uppercase tracking-[0.15em] hover:bg-white hover:text-gray-900 transition-colors duration-200"
                data-testid="homepage-latest-event-cta-button"
              >
                Watch Now
              </button>
            </a>
          </div>
        </div>
      </section>

      {/* ========== SECTION 3: Prophetic Quote ========== */}
      <section className="py-24 sm:py-32 lg:py-40" data-testid="homepage-quote-section">
        <div className="mx-auto w-full max-w-3xl px-6 sm:px-10 text-center">
          <p
            className="font-heading text-xl sm:text-2xl md:text-[28px] text-gray-800 leading-relaxed sm:leading-relaxed italic"
            data-testid="homepage-quote-text"
          >
            "There is no time man ever sought out God, but God who sought
            out man. There is no powerful prayer, but a powerful God who
            answers prayer. There is no powerful man of God, just a powerful
            God in man."
          </p>

          <p className="mt-8 font-heading text-sm text-gray-400 tracking-wide" data-testid="homepage-quote-attribution">
            &mdash; Prophet Malluc
          </p>
        </div>
      </section>

      {/* ========== SECTION 4: Upcoming Events (Optional grid) ========== */}
      {upcomingEvents.length > 1 && (
        <section className="pb-24 sm:pb-32" data-testid="homepage-upcoming-events-section">
          <div className="mx-auto w-full max-w-5xl px-6 sm:px-10">
            <div className="border-t border-gray-100 pt-16">
              <div className="text-center mb-12">
                <p className="text-[11px] uppercase tracking-[0.2em] text-gray-400 font-body font-medium mb-3">
                  Upcoming
                </p>
                <h2 className="font-heading text-3xl sm:text-4xl font-medium text-gray-900">
                  More Events
                </h2>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {upcomingEvents.slice(1, 4).map(event => (
                  <Link
                    key={event.id}
                    to={`/events/${event.id}`}
                    className="group block"
                    data-testid={`homepage-event-card-${event.id}`}
                  >
                    <div className="overflow-hidden bg-[#faf9f7]">
                      <div className="aspect-[4/3] overflow-hidden">
                        <img
                          src={event.hero_image_url || 'https://images.unsplash.com/photo-1701643561845-5802af626884?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=600'}
                          alt={event.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      </div>
                      <div className="p-5">
                        <p className="text-[10px] uppercase tracking-[0.15em] text-gray-400 font-body font-medium">
                          {event.category}
                        </p>
                        <h3 className="font-heading text-lg font-medium text-gray-900 mt-1 group-hover:text-gray-600 transition-colors duration-200">
                          {event.title}
                        </h3>
                        <p className="text-[13px] text-gray-400 font-body mt-2">
                          {formatDate(event.start_at)}
                        </p>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
